/**
 * Cloud Sync Settings Component
 *
 * UI component for managing cloud synchronization (optional Supabase sync)
 * - Shows current mode (local/cloud)
 * - Allows enabling/disabling cloud sync
 * - Handles authentication (signup/signin)
 * - Shows migration progress
 * - Allows local backup export
 */

import { useState } from 'react';
import { useHybridStorage } from '../hooks/useHybridStorage';
import { migrationService, MigrationProgress } from '../services/migrationService';
import { supabase } from '../lib/supabase/client';
import { Icons } from './Icons';
import { useToast } from './Toast';
import { useConfirmation } from '../hooks/useConfirmation';
import { ConfirmationModal } from './ui/ConfirmationModal';

interface CloudSyncSettingsProps {
  className?: string;
}

export function CloudSyncSettings({ className }: CloudSyncSettingsProps) {
  const toast = useToast();
  const { confirm, confirmationProps } = useConfirmation();
  const {
    mode,
    isAuthenticated,
    cloudEnabled,
    userId,
    enableCloudSync,
    disableCloudSync,
  } = useHybridStorage();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signup' | 'signin'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [migrationProgress, setMigrationProgress] = useState<MigrationProgress | null>(null);
  const [migrating, setMigrating] = useState(false);

  /**
   * Handle signup
   */
  const handleSignUp = async () => {
    if (!email || !password) {
      setError('Email et mot de passe requis');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (signUpError) throw signUpError;

      if (data.user && data.session) {
        // Email confirmation NOT required - user is logged in
        setShowAuthModal(false);
      } else if (data.user && !data.session) {
        // Email confirmation required - user created but not logged in
        setError('Compte créé ! Vérifiez votre email pour confirmer votre compte, puis connectez-vous.');
        setAuthMode('signin'); // Switch to signin mode
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle signin
   */
  const handleSignIn = async () => {
    if (!email || !password) {
      setError('Email et mot de passe requis');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;

      if (data.user) {
        setShowAuthModal(false);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de connexion';
      // Translate common errors to French
      if (message.includes('Invalid login credentials')) {
        setError('Email ou mot de passe incorrect');
      } else if (message.includes('Email not confirmed')) {
        setError('Email non confirmé. Vérifiez votre boîte mail.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle cloud sync activation
   */
  const handleEnableCloudSync = async () => {
    if (!isAuthenticated || !userId) {
      // Show auth modal first
      setShowAuthModal(true);
      return;
    }

    try {
      setMigrating(true);
      setError(null);

      // Check if user has local data to migrate
      const hasLocalData = migrationService.hasLocalData();

      if (hasLocalData) {
        const confirmed = await confirm({
          title: 'Migration vers le cloud',
          message: 'Vos données locales vont être migrées vers le cloud. Continuer ?',
          variant: 'info',
          confirmText: 'Migrer',
        });
        if (!confirmed) {
          setMigrating(false);
          return;
        }

        // Migrate local data to cloud with progress tracking
        const result = await migrationService.migrateToCloud(userId, (progress) => {
          setMigrationProgress(progress);
        });

        if (!result.success) {
          throw new Error(`Migration failed: ${result.errors.join(', ')}`);
        }
      }

      // Enable cloud sync
      await enableCloudSync();

      toast.success('Cloud Sync activé ! Vos données sont maintenant synchronisées.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'activation');
    } finally {
      setMigrating(false);
      setMigrationProgress(null);
    }
  };

  /**
   * Handle cloud sync deactivation
   */
  const handleDisableCloudSync = async () => {
    const confirmed = await confirm({
      title: 'Désactiver Cloud Sync',
      message: 'Vos données resteront en local (déjà en cache). Continuer ?',
      variant: 'warning',
      confirmText: 'Désactiver',
    });
    if (!confirmed) return;

    try {
      disableCloudSync();
      toast.info('Cloud Sync désactivé. Vous êtes maintenant en mode local uniquement.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la désactivation');
    }
  };

  /**
   * Handle local backup export
   */
  const handleExportToLocal = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      const result = await migrationService.exportToLocal(userId);

      if (!result.success) {
        throw new Error(`Export failed: ${result.errors.join(', ')}`);
      }

      toast.success(`Backup local créé ! ${result.itemsMigrated} éléments exportés.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'export');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle signout
   */
  const handleSignOut = async () => {
    const confirmed = await confirm({
      title: 'Se déconnecter',
      message: 'Vous serez déconnecté de votre compte. Vos données locales restent intactes.',
      variant: 'warning',
      confirmText: 'Déconnexion',
    });
    if (!confirmed) return;

    try {
      await supabase.auth.signOut();
      disableCloudSync();
    } catch {
      // Silent fail on signout
    }
  };

  return (
    <div className={className} style={{
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '12px',
      padding: '24px',
    }}>
      <h2 style={{
        fontSize: '18px',
        fontWeight: 600,
        color: 'var(--text)',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <Icons.Cloud size={20} />
        Synchronisation Cloud
      </h2>

      {/* Current Mode Badge */}
      <div style={{
        padding: '16px',
        background: 'var(--bg)',
        borderRadius: '8px',
        marginBottom: '16px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <p style={{ color: 'var(--muted)', fontSize: '14px', marginBottom: '4px' }}>
              Mode actuel
            </p>
            {!isAuthenticated ? (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                background: 'var(--bg)',
                color: 'var(--muted)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 600,
              }}>
                🏠 Local uniquement
              </span>
            ) : cloudEnabled ? (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                background: 'var(--primary-bg)',
                color: 'var(--primary)',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 600,
              }}>
                ☁️ Cloud Sync activé
              </span>
            ) : (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                background: 'var(--success-bg)',
                color: 'var(--success)',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 600,
              }}>
                ✅ Connecté - Cloud disponible
              </span>
            )}
          </div>
          {/* User email when authenticated */}
          {isAuthenticated && userId && (
            <div style={{
              fontSize: '12px',
              color: 'var(--muted)',
              textAlign: 'right',
            }}>
              <span style={{ color: 'var(--success)' }}>●</span> Connecté
            </div>
          )}
        </div>
      </div>

      {/* Migration Progress */}
      {migrating && migrationProgress && (
        <div style={{
          padding: '16px',
          background: 'var(--primary-bg)',
          border: '1px solid var(--primary)',
          borderRadius: '8px',
          marginBottom: '16px',
        }}>
          <p style={{ color: 'var(--primary)', fontSize: '14px', marginBottom: '8px' }}>
            {migrationProgress.stage}
          </p>
          <div style={{
            width: '100%',
            height: '8px',
            background: 'var(--bg)',
            borderRadius: '4px',
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${migrationProgress.percentage}%`,
              height: '100%',
              background: 'var(--primary)',
              transition: 'width 0.3s ease',
            }} />
          </div>
          <p style={{ color: 'var(--muted)', fontSize: '12px', marginTop: '4px' }}>
            {migrationProgress.current} / {migrationProgress.total}
          </p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={{
          padding: '12px',
          background: 'var(--error-bg)',
          border: '1px solid var(--error)',
          borderRadius: '8px',
          marginBottom: '16px',
        }}>
          <p style={{ color: 'var(--error)', fontSize: '14px' }}>
            ⚠️ {error}
          </p>
        </div>
      )}

      {/* Not Authenticated - Show Benefits & CTA */}
      {!isAuthenticated && (
        <div style={{ marginTop: '16px' }}>
          <p style={{ color: 'var(--text)', fontSize: '14px', marginBottom: '12px' }}>
            Activez la synchronisation cloud pour :
          </p>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: '0 0 16px 0',
            color: 'var(--muted)',
            fontSize: '14px',
          }}>
            <li style={{ padding: '6px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: 'var(--success)' }}>✅</span>
              Sync multi-devices (PC, mobile, tablette)
            </li>
            <li style={{ padding: '6px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: 'var(--success)' }}>✅</span>
              Backup automatique de vos données
            </li>
            <li style={{ padding: '6px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: 'var(--success)' }}>✅</span>
              Accès depuis n'importe où
            </li>
            <li style={{ padding: '6px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: 'var(--success)' }}>✅</span>
              Real-time synchronization
            </li>
          </ul>

          <button
            onClick={() => setShowAuthModal(true)}
            disabled={loading || migrating}
            style={{
              width: '100%',
              padding: '12px',
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Icons.Cloud size={16} />
            Créer un compte et activer Cloud Sync
          </button>

          <p style={{
            color: 'var(--muted)',
            fontSize: '12px',
            marginTop: '8px',
            textAlign: 'center',
          }}>
            ℹ️ Vos données locales seront migrées vers le cloud
          </p>
        </div>
      )}

      {/* Authenticated but Cloud Sync Not Enabled */}
      {isAuthenticated && !cloudEnabled && (
        <div style={{ marginTop: '16px' }}>
          <p style={{ color: 'var(--text)', fontSize: '14px', marginBottom: '12px' }}>
            Vous êtes connecté mais Cloud Sync est désactivé.
          </p>

          <button
            onClick={handleEnableCloudSync}
            disabled={loading || migrating}
            style={{
              width: '100%',
              padding: '12px',
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {migrating ? 'Migration en cours...' : 'Activer Cloud Sync'}
          </button>
        </div>
      )}

      {/* Authenticated and Cloud Sync Enabled */}
      {isAuthenticated && cloudEnabled && (
        <div style={{ marginTop: '16px' }}>
          <p style={{
            color: 'var(--success)',
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <span style={{ fontSize: '16px' }}>✅</span>
            Cloud Sync activé
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button
              onClick={handleExportToLocal}
              disabled={loading || migrating}
              style={{
                width: '100%',
                padding: '10px',
                background: 'var(--bg)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <Icons.Download size={16} />
              📥 Créer backup local
            </button>

            <button
              onClick={handleDisableCloudSync}
              disabled={loading || migrating}
              style={{
                width: '100%',
                padding: '10px',
                background: 'var(--bg)',
                color: 'var(--warning)',
                border: '1px solid var(--warning)',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              Désactiver Cloud Sync
            </button>

            <button
              onClick={handleSignOut}
              disabled={loading || migrating}
              style={{
                width: '100%',
                padding: '10px',
                background: 'var(--bg)',
                color: 'var(--muted)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Se déconnecter
            </button>
          </div>

          <p style={{
            color: 'var(--muted)',
            fontSize: '12px',
            marginTop: '12px',
            textAlign: 'center',
          }}>
            ℹ️ Désactiver le cloud sync ne supprime pas vos données locales
          </p>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 600,
              color: 'var(--text)',
              marginBottom: '16px',
            }}>
              {authMode === 'signup' ? 'Créer un compte' : 'Se connecter'}
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: 'var(--text)',
                fontSize: '14px',
                marginBottom: '6px',
              }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--text)',
                  fontSize: '14px',
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: 'var(--text)',
                fontSize: '14px',
                marginBottom: '6px',
              }}>
                Mot de passe
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--text)',
                  fontSize: '14px',
                }}
              />
            </div>

            {error && (
              <div style={{
                padding: '10px',
                background: 'var(--error-bg)',
                border: '1px solid var(--error)',
                borderRadius: '8px',
                marginBottom: '16px',
              }}>
                <p style={{ color: 'var(--error)', fontSize: '13px' }}>
                  {error}
                </p>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                onClick={authMode === 'signup' ? handleSignUp : handleSignIn}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {loading ? 'Chargement...' : authMode === 'signup' ? 'Créer le compte' : 'Se connecter'}
              </button>

              <button
                onClick={() => setAuthMode(authMode === 'signup' ? 'signin' : 'signup')}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'transparent',
                  color: 'var(--primary)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                {authMode === 'signup' ? 'Déjà un compte ? Se connecter' : 'Créer un nouveau compte'}
              </button>

              <button
                onClick={() => {
                  setShowAuthModal(false);
                  setError(null);
                }}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'transparent',
                  color: 'var(--muted)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* V2.I.10.d : confirmation modal replaces native confirm() */}
      <ConfirmationModal {...confirmationProps} />
    </div>
  );
}
